import type { TFunction } from "i18next";

export type DisplayAccount = { name?: string | null; type?: string | null; kind?: string | null };
type Mapping = readonly [name: string, type: string, kind: string, key: string, english: string];

// Recognition names are stored inputs, independent of approved English display values.
export const ACCOUNT_NAME_MAPPINGS: readonly Mapping[] = [
  ["Non-inventory COGS", "cogs", "non_inventory_cogs", "accountNames.nonInventoryCogs", "Non-inventory COGS"],
  ["Accounts Receivable", "asset", "ar", "accountNames.accountsReceivable", "Accounts Receivable"],
  ["Machinery & equipment", "asset", "fixed_asset", "accountNames.machineryEquipment", "Machinery & equipment"],
  ["Accumulated Depreciation", "asset", "contra_asset", "accountNames.accumulatedDepreciation", "Accumulated Depreciation"],
  ["Depreciation Expense", "expense", "depreciation", "accountNames.depreciationExpense", "Depreciation Expense"],
  ["Prepaid Expenses", "asset", "prepaid", "accountNames.prepaidExpenses", "Prepaid Expenses"],
  ["Customer Deposits", "liability", "customer_deposits", "accountNames.customerDeposits", "Customer Deposits"],
  ["Vendor Deposits", "asset", "vendor_deposits", "accountNames.vendorDeposits", "Vendor Deposits"],
  ["Shareholder Advance", "liability", "shareholder_advance", "accountNames.shareholderAdvance", "Owner Advance"],
  ["Due from Shareholder", "asset", "due_from_shareholder", "accountNames.dueFromShareholder", "Due from Owner"],
  ["Shareholder Capital", "equity", "shareholder_capital", "accountNames.shareholderCapital", "Owner Capital"],
  ["Opening Balance Equity", "equity", "opening_balance_equity", "accountNames.openingBalanceEquity", "Opening Balance Equity"],
  ["Gain on Disposal", "income", "gain_on_disposal", "accountNames.gainOnDisposal", "Gain on Disposal"],
  ["Loss on Disposal", "expense", "loss_on_disposal", "accountNames.lossOnDisposal", "Loss on Disposal"],
  ["Accounts Payable", "liability", "ap", "accountNames.accountsPayable", "Accounts Payable"],
  ["Preorder Liability", "liability", "preorder", "accountNames.preorderLiability", "Preorder Liability"],
  ["Bank Loan Payable", "liability", "bank_loan_payable", "accountNames.bankLoanPayable", "Bank Loan Payable"],
  ["Sales Revenue", "income", "revenue", "accountNames.salesRevenue", "Sales Revenue"],
  ["Miscellaneous Income", "income", "other_income", "accountNames.miscellaneousIncome", "Miscellaneous Income"],
  ["COGS", "cogs", "cogs", "accountNames.cogs", "COGS"],
  ["Payment Processing Fees", "expense", "payment_processing_fee", "accountNames.paymentProcessingFees", "Payment Processing Fees"],
  ["Platform Service Fees", "expense", "nimos_platform_fee", "accountNames.platformServiceFees", "Platform Service Fees"],
  ["Rent", "expense", "expense", "accountNames.rent", "Rent"],
  ["Salary", "expense", "expense", "accountNames.salary", "Salary"],
  ["Supplies", "expense", "expense", "accountNames.supplies", "Supplies"],
  ["Subscriptions & Utilities", "expense", "expense", "accountNames.subscriptionsUtilities", "Subscriptions & Utilities"],
  ["Marketing", "expense", "expense", "accountNames.marketing", "Marketing"],
  ["Meals & Entertainment", "expense", "expense", "accountNames.mealsEntertainment", "Meals & Entertainment"],
  ["Travel", "expense", "expense", "accountNames.travel", "Travel"],
  ["Bank Charges", "expense", "expense", "accountNames.bankCharges", "Bank Charges"],
  ["Interest Expense", "expense", "expense", "accountNames.interestExpense", "Interest Expense"],
  ["Miscellaneous", "expense", "expense", "accountNames.miscellaneous", "Miscellaneous"],
  ["Owner's Capital", "equity", "shareholder_capital", "accountNames.ownersCapital", "Owner Capital"],
  ["Gain on Disposal of Assets", "other_income", "system", "accountNames.gainOnDisposalOfAssets", "Gain on Disposal of Assets"],
  ["Loss on Disposal of Assets", "other_expense", "system", "accountNames.lossOnDisposalOfAssets", "Loss on Disposal of Assets"],
  ["Inventory", "asset", "inventory", "balanceSheet.inventory", "Inventory"],
  ["Retained Earnings", "equity", "retained_earnings", "balanceSheet.retainedEarnings", "Retained Earnings"],
  ["Credit Card Payable", "liability", "credit_card_payable", "payables.creditCardPayable", "Credit Card Payable"],
  ["Fixed Asset – Computer", "asset", "fixed_asset", "newFixedAsset.kindAccounts.computer", "Fixed Asset – Computer"],
  ["Fixed Asset – Leasehold Improvements", "asset", "fixed_asset", "newFixedAsset.kindAccounts.leasehold", "Fixed Asset – Leasehold Improvements"],
  ["Fixed Asset – Equipment & Fixtures", "asset", "fixed_asset", "newFixedAsset.kindAccounts.equipmentFixtures", "Fixed Asset – Equipment & Fixtures"],
  ["Fixed Asset – Other", "asset", "fixed_asset", "newFixedAsset.kindAccounts.other", "Fixed Asset – Other"],
];

function translated(t: TFunction, key: string, english: string): string {
  const value = t(key as never, { defaultValue: english });
  return typeof value === "string" && value && value !== key ? value : english;
}

/** Pure display projection. No normalization, provenance inference, or object mutation. */
export function localizedAccountDisplayName(
  account: DisplayAccount | null | undefined, t: TFunction, fallback: string,
  mappings: readonly Mapping[] = ACCOUNT_NAME_MAPPINGS,
): string {
  if (!account || !account.name || !account.type || !account.kind) return fallback;
  const matches = mappings.filter(([name, type, kind]) =>
    account.name === name && account.type === type && account.kind === kind);
  if (matches.length !== 1) return fallback;
  const [, , , key, english] = matches[0];
  return translated(t, key, english);
}

const PRESENTATIONS: Readonly<Record<string, readonly [string, string]>> = {
  "balance_sheet.current_year_profit_loss": ["accountNames.currentYearProfitLoss", "Current Year Profit/Loss"],
  "income_statement.disposal_gain": ["accountNames.gainOnDisposalOfAsset", "Gain on Disposal of Asset"],
  "income_statement.disposal_loss": ["accountNames.lossOnDisposalOfAsset", "Loss on Disposal of Asset"],
};

export function reportAccountDisplayName(
  account: DisplayAccount & { account_id?: number | null; presentation_key?: string | null },
  t: TFunction, fallback: string,
): string {
  if (account.presentation_key != null) {
    const mapping = Object.prototype.hasOwnProperty.call(PRESENTATIONS, account.presentation_key)
      ? PRESENTATIONS[account.presentation_key] : undefined;
    if (!mapping || account.name !== mapping[1] || account.account_id != null) return fallback;
    const balanceSheet = account.presentation_key === "balance_sheet.current_year_profit_loss";
    if (balanceSheet ? (account.type !== "equity" || account.kind !== "current_year_pl")
      : (account.type != null || account.kind != null)) return fallback;
    return translated(t, mapping[0], mapping[1]);
  }
  return localizedAccountDisplayName(account, t, fallback);
}

/** Only the explicit payment alias; the caller must verify its raw ID association. */
export function creditCardPaymentDisplayName(account: DisplayAccount | undefined, t: TFunction, fallback: string): string {
  return account?.name === "Credit Card Payable" && account.type === "liability" && account.kind === "credit_card_payable"
    ? translated(t, "expenses.entry.creditCard", "Credit Card") : fallback;
}

/** An ambiguous ID or disagreeing row name must never select arbitrary metadata. */
export function uniqueDisplayAccount<T extends DisplayAccount & { id: string | number }>(
  accounts: readonly T[], id: string | number | null | undefined, name?: string,
): T | undefined {
  if (id == null) return undefined;
  const matches = accounts.filter(a => String(a.id) === String(id));
  return matches.length === 1 && (name === undefined || matches[0].name === name) ? matches[0] : undefined;
}
